import React from 'react';
import { useTheme } from '@mui/material';

interface ReviewsMetricsInput {
    ratingDistribution?: { rating: number; count: number }[];
}

export function useReviewsMetricsData(input: ReviewsMetricsInput) {
    const theme = useTheme();

    const maxDistribution = Math.max(...(input.ratingDistribution?.map(d => d.count) ?? [0]));

    const chartData = React.useMemo(
        () => ({
            labels: input.ratingDistribution?.map(d => `${d.rating}★`) ?? [],
            datasets: [
                {
                    data: input.ratingDistribution?.map(d => (d.count === 0 ? null : d.count)) ?? [],
                    backgroundColor: theme.palette.purple.core,
                    borderRadius: 20,
                    borderSkipped: false,
                    barThickness: 8
                }
            ]
        }),
        [input.ratingDistribution, theme]
    );

    const chartOptions = React.useMemo(
        () => ({
            indexAxis: 'y' as const,
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { right: 25 } },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true }
            },
            scales: {
                x: {
                    display: false,
                    max: maxDistribution * 1.2
                },
                y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                        font: { size: 12, weight: 600 as const },
                        color: theme.palette.text.secondary
                    }
                }
            }
        }),
        [maxDistribution, theme]
    );

    const distributionPlugin = React.useMemo(
        () => [
            {
                id: 'barBackground',
                beforeDatasetsDraw(chart: {
                    getDatasetMeta: (i: number) => {
                        data: { x: number; y: number; height: number; base: number }[];
                    };
                    ctx: CanvasRenderingContext2D;
                    chartArea: { right: number };
                }) {
                    const { ctx, chartArea } = chart;
                    const meta = chart.getDatasetMeta(0);
                    ctx.save();
                    ctx.fillStyle = theme.palette.purple.tertiary;
                    ctx.globalAlpha = 0.25;
                    meta.data.forEach((bar: { x: number; y: number; height: number; base: number }) => {
                        const barHeight = bar.height;
                        ctx.beginPath();
                        ctx.roundRect(
                            bar.base,
                            bar.y - barHeight / 2,
                            chartArea.right - bar.base,
                            barHeight,
                            20
                        );
                        ctx.fill();
                    });
                    ctx.restore();
                }
            },
            {
                id: 'barLabels',
                afterDatasetsDraw(chart: {
                    getDatasetMeta: (i: number) => { data: { x: number; y: number }[] };
                    ctx: CanvasRenderingContext2D;
                    chartArea: { right: number };
                }) {
                    const { ctx, chartArea } = chart;
                    const meta = chart.getDatasetMeta(0);
                    ctx.save();
                    ctx.font = '12px Ubuntu, Helvetica Neue, sans-serif';
                    ctx.fillStyle = theme.palette.text.secondary;
                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'middle';
                    meta.data.forEach((bar: { x: number; y: number }, index: number) => {
                        ctx.fillText(
                            String(input.ratingDistribution?.[index]?.count ?? 0),
                            chartArea.right + 25,
                            bar.y
                        );
                    });
                    ctx.restore();
                }
            }
        ],
        [input.ratingDistribution, theme]
    );

    return {
        chartData,
        chartOptions,
        distributionPlugin
    };
}
